import React, { useRef, useState, useEffect, useImperativeHandle } from "react";
import PropTypes from "prop-types";
import classes from "./AudioWaveformPlayer.scss";
import { allowURLWithCredentials } from "Utils/SecureUrlHelpers.js";
import classNames from "classnames";
import { Button, Dropdown, DropdownMenu, IconButton } from "@toddle-edu/ds-web";
import { PlayFilled, PauseFilled } from "@toddle-edu/ds-icons";

import WaveSurfer from "wavesurfer.js";
import moment from "moment";

const KEYBOARD_AUDIO_JUMP_SECONDS = 5;

const SIZE_BUTTON_MAP = {
  medium: "medium",
  small: "x-small",
};

const DURATION_CONTAINER_TEXT = {
  medium: "text-body",
  small: "text-body-s",
};

const WAVEFORM_HEIGHT_MAP = {
  medium: 45,
  small: 24,
};

const AUDIO_SPEED_OPTIONS = [
  {
    key: "dropdown_title",
    label: "Playback speed",
    isItemGroup: true,
    options: [
      {
        key: "0.25",
        label: "0.25x",
      },
      {
        key: "0.5",
        label: "0.5x",
      },
      {
        key: "0.75",
        label: "0.75x",
      },
      {
        key: "1",
        label: "1x",
      },
      {
        key: "1.5",
        label: "1.5x",
      },
      {
        key: "2",
        label: "2x",
      },
    ],
  },
];

const PlayerButton = props => {
  const { handleClick, mediaType, playing, isUploading, testId } = props;
  const playerButtonClass = classNames(classes.playerButton, {
    [classes.secondaryPlayerButton]: mediaType === "secondary",
    [classes.audioUploading]: isUploading,
  });
  return (
    <IconButton
      icon={
        playing ? (
          <PauseFilled size={"xx-small"} />
        ) : (
          <PlayFilled size={"xx-small"} />
        )
      }
      size={"medium"}
      variant={"plain"}
      onClick={e => {
        e.stopPropagation();
        handleClick();
      }}
      className={playerButtonClass}
      disabled={isUploading}
      testId={`${testId}-audioWaveformPlayer-togglePlayPause`}
      aria-label={playing ? "Pause recording" : "Play recording"}
    />
  );
};

const roundRectIsSupported = () => {
  const canvasElement = document.createElement("canvas");
  const context = canvasElement.getContext("2d");

  if (context && typeof context.roundRect === "function") return true;
  return false;
};

/**
 * Custom wavesurfer hook
 */
const useWavesurfer = (waveFormContainerRef, blob, height = 45) => {
  const [wavesurfer, setWavesurfer] = useState(null);

  useEffect(() => {
    if (!waveFormContainerRef.current) return;

    if (!blob) return;

    const wavesurferInstance = WaveSurfer.create({
      barGap: 2,
      barRadius: roundRectIsSupported() ? 2 : 0,
      barWidth: 2,
      cursorColor: "#222222",
      cursorWidth: 8,
      fetchParams: {
        credentials: allowURLWithCredentials(blob.url)
          ? "include"
          : "same-origin",
      },
      fillParent: true,
      height,
      progressColor: "#222222",
      url: blob.url,
      waveColor: "#DBDBDB",
      container: waveFormContainerRef.current,
    });

    setWavesurfer(wavesurferInstance);

    return () => {
      wavesurferInstance?.destroy();
    };
  }, [blob, waveFormContainerRef]);

  return wavesurfer;
};

const useCurrentTime = wavesurfer => {
  const [currentTime, setCurrentTime] = useState(0);

  useEffect(() => {
    if (!wavesurfer) return;

    const handleTimeUpdate = currentTime => {
      setCurrentTime(Math.floor(currentTime));
    };

    const unsubscribe = wavesurfer.on("timeupdate", handleTimeUpdate);

    return () => {
      unsubscribe();
    };
  }, [wavesurfer]);

  return currentTime;
};

const formatTime = seconds => {
  return moment.duration(seconds, "seconds").format("mm:ss", { trim: false });
};

const AudioWaveformPlayerV1 = React.forwardRef((props, ref) => {
  const {
    attachment,
    audioLoaded,
    audioPlaybackTime = 0,
    mediaType,
    isUploading,
    showCurrentTime = false,
    testId,
  } = props;

  const [duration, setDuration] = useState("00:00");
  const [playing, setPlaying] = useState(false);
  const [audioSpeed, setAudioSpeed] = useState("1");

  const waveFormContainerRef = useRef(null);
  const wavesurfer = useWavesurfer(waveFormContainerRef, attachment);
  // "timeupdate" event is called very frequently,
  // thus reusing the logic to get current time in a custom hook to
  // have a single source of truth for current time and avoid unnecessary re-renders of the component
  const currentTime = useCurrentTime(wavesurfer);

  useImperativeHandle(ref, () => ({
    /**
     * Toggles the audio playback state
     */
    toggleAudio: () => {
      wavesurfer?.playPause();
    },
    /**
     * Sets the audio time at which the audio should start playing
     * @returns {number} Returns the current time of the audio in seconds
     */
    getCurrentTime: () => {
      return wavesurfer?.getCurrentTime();
    },
  }));

  useEffect(() => {
    if (!wavesurfer) return;

    setPlaying(false);
    setDuration("00:00");
    setAudioSpeed("1");

    // seek to a specific time if prop provided
    wavesurfer.seekTo(audioPlaybackTime);

    const subscriptions = [
      // subscribe to when waveform is mounted and ready event
      wavesurfer.on("ready", duration => {
        const totalDurationInSeconds = ~~duration;
        const currentDuration = moment
          .duration(totalDurationInSeconds, "seconds")
          .format("mm:ss", { trim: false });

        audioLoaded();
        setDuration(prevDuration => {
          return currentDuration !== prevDuration && currentDuration !== "00:00"
            ? currentDuration
            : prevDuration;
        });
      }),

      //subscribe to play and pause events
      wavesurfer.on("play", () => {
        setPlaying(true);
      }),
      wavesurfer.on("pause", () => {
        setPlaying(false);
      }),

      wavesurfer.on("finish", () => {
        wavesurfer.setTime(0);
      }),
    ];

    return () => {
      subscriptions.forEach(unsubscribe => unsubscribe());
    };
  }, [wavesurfer, audioPlaybackTime]);

  const changeAudioSpeed = e => {
    setAudioSpeed(e.key);
    wavesurfer.setPlaybackRate(e.key);
  };

  const togglePlayPause = () => {
    if (playing) {
      wavesurfer.pause();
      return;
    }
    wavesurfer.play();
  };

  const handleWaveformKeyDown = e => {
    e.stopPropagation();

    if (!wavesurfer) return;

    const currentTime = wavesurfer.getCurrentTime();

    switch (e.key) {
      case "ArrowRight":
        wavesurfer.setTime(currentTime + KEYBOARD_AUDIO_JUMP_SECONDS);
        if (playing) {
          wavesurfer.play();
        }
        break;
      case "ArrowLeft":
        wavesurfer.setTime(
          Math.max(0, currentTime - KEYBOARD_AUDIO_JUMP_SECONDS)
        );
        if (playing) {
          wavesurfer.play();
        }
        break;
      case " ":
        e.preventDefault();
        togglePlayPause();
        break;
      case "Enter":
        togglePlayPause();
        break;
      default:
        break;
    }
  };

  const durationContainerClasses = classNames(
    classes.durationContainer,
    "text-body-2"
  );

  const currentTimeAndDurationText = (() => {
    if (!showCurrentTime) return duration;

    return `${formatTime(currentTime)} / ${duration}`;
  })();

  return (
    <div data-ds-version="1.0" className={classes.visualContainer}>
      <div className={classes.buttonContainer}>
        <PlayerButton
          playing={playing}
          handleClick={() => wavesurfer.playPause()}
          mediaType={mediaType}
          isUploading={isUploading}
          testId={testId}
        />
      </div>
      <div
        id="waveform"
        className={classes.waveformContainer}
        ref={waveFormContainerRef}
        onClick={e => e.stopPropagation()}
        onMouseDown={e => e.stopPropagation()}
        onKeyDown={handleWaveformKeyDown}
        aria-live="polite"
        aria-valuemin={0}
        aria-valuemax={wavesurfer ? Math.floor(wavesurfer.getDuration()) : 0}
        aria-valuenow={currentTime}
        aria-valuetext={`${formatTime(currentTime)} of ${duration}`}
        role="slider"
        aria-label="Audio seeker, use left and right arrow keys to seek backward and forward"
        tabIndex={0}
        data-test-id={`${testId}-audioWaveformPlayer-seeker`}
      ></div>
      <div
        className={durationContainerClasses}
        onClick={e => e.stopPropagation()}
        role="presentation"
      >
        {currentTimeAndDurationText}
      </div>
      <div
        className={classes.audioSpeedControl}
        // to prevent audio from playing when clicking on the dropdown
        onClick={e => e.stopPropagation()}
        onKeyDown={e => e.stopPropagation()}
        role="presentation"
      >
        <Dropdown
          getPopupContainer={() => document.body}
          overlay={
            <DropdownMenu
              showSelection={true}
              value={audioSpeed}
              onClick={changeAudioSpeed}
              options={AUDIO_SPEED_OPTIONS}
              testId={`${testId}-audioWaveformPlayer-audioSpeeedControl`}
            />
          }
        >
          <Button
            size="small"
            isFullWidth={true}
            testId={`${testId}-audioWaveformPlayer-audioSpeedControl`}
            _legacy_variant="outlined-subtle"
            type="outlined"
            variant="neutral"
            aria-label={`Change playback speed, current speed is ${audioSpeed}x`}
          >
            {`${audioSpeed}x`}
          </Button>
        </Dropdown>
      </div>
    </div>
  );
});

// This component will be used inside a wrapper component such as MediaCardV2, that's why styles are named with mediaCard prefix
const AudioWaveformPlayerV2 = React.forwardRef((props, ref) => {
  const {
    attachment,
    audioLoaded,
    audioPlaybackTime = 0,
    isUploading,
    showCurrentTime = false,
    testId,
    playAudioOnSeek,
    size = "medium",
  } = props;

  const [duration, setDuration] = useState("00:00");
  const [playing, setPlaying] = useState(false);
  const [audioSpeed, setAudioSpeed] = useState("1");

  const waveFormContainerRef = useRef(null);
  const wavesurfer = useWavesurfer(
    waveFormContainerRef,
    attachment,
    WAVEFORM_HEIGHT_MAP[size]
  );
  // "timeupdate" event is called very frequently,
  // thus reusing the logic to get current time in a custom hook to
  // have a single source of truth for current time and avoid unnecessary re-renders of the component
  const currentTime = useCurrentTime(wavesurfer);

  useImperativeHandle(ref, () => ({
    /**
     * Toggles the audio playback state
     */
    toggleAudio: () => {
      wavesurfer?.playPause();
    },
    /**
     * Sets the audio time at which the audio should start playing
     * @returns {number} Returns the current time of the audio in seconds
     */
    getCurrentTime: () => {
      return wavesurfer?.getCurrentTime();
    },
  }));

  useEffect(() => {
    if (!wavesurfer) return;

    setPlaying(false);
    setDuration("00:00");
    setAudioSpeed("1");

    // seek to a specific time if prop provided
    wavesurfer.seekTo(audioPlaybackTime);

    const subscriptions = [
      // subscribe to when waveform is mounted and ready event
      wavesurfer.on("ready", duration => {
        const totalDurationInSeconds = ~~duration;
        const currentDuration = moment
          .duration(totalDurationInSeconds, "seconds")
          .format("mm:ss", { trim: false });

        audioLoaded();

        setDuration(prevDuration => {
          return currentDuration !== prevDuration && currentDuration !== "00:00"
            ? currentDuration
            : prevDuration;
        });
      }),

      //subscribe to play and pause events
      wavesurfer.on("play", () => {
        setPlaying(true);
      }),
      wavesurfer.on("pause", () => {
        setPlaying(false);
      }),

      // subsribe to seek event
      wavesurfer.on("seeking", () => {
        if (playAudioOnSeek) return;
        wavesurfer.pause();
      }),

      wavesurfer.on("finish", () => {
        wavesurfer.setTime(0);
      }),
    ];

    return () => {
      subscriptions.forEach(unsubscribe => unsubscribe());
    };
  }, [wavesurfer, audioPlaybackTime]);

  const changeAudioSpeed = e => {
    setAudioSpeed(e.key);
    wavesurfer.setPlaybackRate(e.key);
  };

  const durationContainerClasses = classNames(
    classes.mediaCardDurationContainer,
    DURATION_CONTAINER_TEXT[size]
  );

  const containerClasses = classNames(
    classes.mediaCardVisualContainer,
    classes[size]
  );

  const handlePlayerButtonClick = e => {
    e.stopPropagation();
    wavesurfer.playPause();
  };

  const getPlayButtonIcon = () => {
    const PlayerButtonIcon = playing ? PauseFilled : PlayFilled;

    return <PlayerButtonIcon className="icon-primary" />;
  };

  const togglePlayPause = () => {
    if (playing) {
      wavesurfer.pause();
      return;
    }
    wavesurfer.play();
  };

  const handleWaveformKeyDown = e => {
    e.stopPropagation();

    if (!wavesurfer) return;

    const currentTime = wavesurfer.getCurrentTime();

    switch (e.key) {
      case "ArrowRight":
        wavesurfer.setTime(currentTime + KEYBOARD_AUDIO_JUMP_SECONDS);
        if (playing) {
          wavesurfer.play();
        }
        break;
      case "ArrowLeft":
        wavesurfer.setTime(
          Math.max(0, currentTime - KEYBOARD_AUDIO_JUMP_SECONDS)
        );
        if (playing) {
          wavesurfer.play();
        }
        break;
      case " ":
        e.preventDefault();
        togglePlayPause();
        break;
      case "Enter":
        togglePlayPause();
        break;
      default:
        break;
    }
  };

  const currentTimeAndDurationText = (() => {
    if (!showCurrentTime) return duration;

    return `${formatTime(currentTime)} / ${duration}`;
  })();

  return (
    <div data-ds-version="2.0" className={containerClasses}>
      <IconButton
        icon={getPlayButtonIcon()}
        size={SIZE_BUTTON_MAP[size]}
        onClick={handlePlayerButtonClick}
        disabled={isUploading}
        variant="neutral"
        type="plain"
        testId={`${testId}-audioWaveformPlayer-togglePlayPause`}
        dsVersion="2.0"
        aria-label={playing ? "Pause recording" : "Play recording"}
      />
      <div
        id="waveform"
        className={classes.waveformContainer}
        ref={waveFormContainerRef}
        onClick={e => e.stopPropagation()}
        onMouseDown={e => e.stopPropagation()}
        onKeyDown={handleWaveformKeyDown}
        aria-live="polite"
        aria-valuemin={0}
        aria-valuemax={wavesurfer ? Math.floor(wavesurfer.getDuration()) : 0}
        aria-valuenow={currentTime}
        aria-valuetext={`${formatTime(currentTime)} of ${duration}`}
        role="slider"
        tabIndex={0}
        aria-label="Audio seeker, use left and right arrow keys to seek backward and forward"
        data-test-id={`${testId}-audioWaveformPlayer-seeker`}
      ></div>
      <div
        className={durationContainerClasses}
        onClick={e => e.stopPropagation()}
        role="presentation"
      >
        {currentTimeAndDurationText}
      </div>
      <div
        className={classes.mediaCardAudioSpeedControl}
        // to prevent audio from playing when clicking on the dropdown
        onClick={e => e.stopPropagation()}
        onKeyDown={e => e.stopPropagation()}
        role="presentation"
      >
        <Dropdown
          getPopupContainer={() => document.body}
          overlay={
            <DropdownMenu
              dsVersion="2.0"
              showSelection={true}
              value={audioSpeed}
              onClick={changeAudioSpeed}
              options={AUDIO_SPEED_OPTIONS}
              testId={`${testId}-audioWaveformPlayer-audioSpeedControl`}
            />
          }
        >
          <Button
            size={SIZE_BUTTON_MAP[size]}
            isFullWidth={true}
            testId={`${testId}-audioWaveformPlayer-audioSpeedControl`}
            type="plain"
            variant="neutral"
            dsVersion="2.0"
            className={classes.speedControlButton}
            aria-label={`Change playback speed, current speed is ${audioSpeed}x`}
          >
            {`${audioSpeed}x`}
          </Button>
        </Dropdown>
      </div>
    </div>
  );
});

AudioWaveformPlayerV1.defaultProps = {
  mediaType: "primary",
  isUploading: false,
  playAudioOnSeek: false,
  showCurrentTime: false,
};

AudioWaveformPlayerV1.propTypes = {
  playAudioOnSeek: PropTypes.bool,
  showCurrentTime: PropTypes.bool,
};

AudioWaveformPlayerV2.defaultProps = {
  isUploading: false,
  playAudioOnSeek: false,
  showCurrentTime: false,
};

AudioWaveformPlayerV2.propTypes = {
  playAudioOnSeek: PropTypes.bool,
  showCurrentTime: PropTypes.bool,
};
const AudioWaveformPlayer = React.forwardRef((props, ref) => {
  const { dsVersion, size, ...rest } = props;

  // Not keeping version from DS context for now, as modules which are using AudioWaverform component might break the expected UI.
  const finalDsVersion = dsVersion ?? "1.0";

  if (finalDsVersion === "2.0") {
    return <AudioWaveformPlayerV2 {...rest} ref={ref} size={size} />;
  }

  return <AudioWaveformPlayerV1 {...rest} ref={ref} />;
});

AudioWaveformPlayerV1.displayName = "AudioWaveformPlayer";
AudioWaveformPlayerV2.displayName = "AudioWaveformPlayer";
AudioWaveformPlayer.displayName = "AudioWaveformPlayer";

export default AudioWaveformPlayer;
